import { useEffect, useState } from "react"
import React from "react";
import { Combobox, EInputTypes, InputColumnTableProps, InputTable, InputTextCustom, formatCurrency } from "medere-ui-framework"
import '../Budget.scss'
import { MedicinesBudgetDetailDTO, defaultMedicinesBudgetDetailDTO, getSubtotalValueDay, getSubtotalValueMonth } from "../../../dto/budget/BudgetDetailDTO";
import { InputTableContainerProps } from "../../../dto/InputTableContainerProps";
import { getMedicines } from "../../../servicesHooks/Budget/useBudgetService";
import { ComboMPPDTO, defaultComboMPPDTO } from "../../../dto/budget/ComboMPPDTO";
import { BudgetBodyComponent } from "./BudgetBodyComponent";

interface MedicinesTableProps extends InputTableContainerProps {
    items: MedicinesBudgetDetailDTO[];
    onItemsChange?: (items: MedicinesBudgetDetailDTO[]) => void;
}

export default function MedicinesTable ({items, onItemsChange, addToTotal, defaultEarnings, duration, CRUDBehaviour=true, showPriceByItem=true, showEarnings=true}:MedicinesTableProps) {

    const [subtotalValueDay, setSubtotalValueDay] = useState<number>(0);
    const [subtotalValueMonth, setSubtotalValueMonth] = useState<number>(0);

    const [newMedicinesBudgetDetailDTO, setNewMedicinesBudgetDetailDTO] = useState<MedicinesBudgetDetailDTO>(defaultMedicinesBudgetDetailDTO);

    const [localEarnings, setLocalEarnings] = useState<number>(0);

    /*
    Siempre debe ingresar primero las unidades por paquete.
    Cuando se tiene eso, si se ingresa packageqty, se calcula directo los paquetes por el precio unitario.
    Si se tiene la cantidad de unidades, se calcula el proporcional por paquete de esas unidades.
    */
    const calculateTotal = (unitsperpackage:number, packageqty:number, unitsqty:number, unitprice:number, earning:number) => {
      const earnFactor = ((earning ?? 0) + 100) / 100;
      if (unitsperpackage > 0) {
        if (unitsqty === 0) return 0;
        return (unitsqty / unitsperpackage * unitprice) * earnFactor;
      }
      if (packageqty > 0) {
        return packageqty * unitprice * earnFactor;
      }
      // Fallback para items cargados del servidor sin datos de envase:
      // unitsqty * unitprice (el unitprice es precio por unidad en este contexto)
      return unitsqty * unitprice * earnFactor;
    }

    function calculateItem(item:MedicinesBudgetDetailDTO):MedicinesBudgetDetailDTO {
      let totalMonth = calculateTotal(item?.comboMPPDTO?.unitsperpackage??0, item.packageqty, item.unitsqty, item.unitprice, item.earnings);
      return {
        ...item,
        valueday: duration?((duration>0)?(totalMonth/duration):0):0,
        valuemonth: totalMonth
      }
    }

    useEffect(() => {
      setNewMedicinesBudgetDetailDTO({...newMedicinesBudgetDetailDTO, valueday: duration?((duration>0)?newMedicinesBudgetDetailDTO.valuemonth/duration:0):0});
      let subtotal = 0;
      items.forEach((item) => {
        item.valuemonth = calculateTotal(item.comboMPPDTO?.unitsperpackage??0, item.packageqty, item.unitsqty, item.unitprice, item.earnings);
        item.valueday = duration?((duration>0)?item.valuemonth/duration:0):0;
        subtotal += item.valuemonth;
      });
      setSubtotalValueMonth(subtotal);
      setSubtotalValueDay(getSubtotalValueDay(items));
      setNewMedicinesBudgetDetailDTO(defaultMedicinesBudgetDetailDTO);
      if(addToTotal)
        addToTotal(subtotal - subtotalValueMonth)
    },[items, duration]);

    useEffect(() => {
      setLocalEarnings(defaultEarnings ?? 0);
      setNewMedicinesBudgetDetailDTO({...newMedicinesBudgetDetailDTO, earnings: defaultEarnings ?? 0});
    }, [defaultEarnings]);

    useEffect(() => {
      setNewMedicinesBudgetDetailDTO({
        ...newMedicinesBudgetDetailDTO,
        valuemonth: calculateTotal(
          newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage??0,
          newMedicinesBudgetDetailDTO.packageqty,
          newMedicinesBudgetDetailDTO.unitsqty,
          newMedicinesBudgetDetailDTO.unitprice,
          newMedicinesBudgetDetailDTO.earnings
        )});
    },[newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage,
      newMedicinesBudgetDetailDTO.packageqty, newMedicinesBudgetDetailDTO.unitsqty, newMedicinesBudgetDetailDTO.unitprice, newMedicinesBudgetDetailDTO.earnings]);

    useEffect(() => {
      setNewMedicinesBudgetDetailDTO({...newMedicinesBudgetDetailDTO, valueday: duration?((duration>0)?newMedicinesBudgetDetailDTO.valuemonth/duration:0):0});
    },[newMedicinesBudgetDetailDTO.valuemonth, duration]);

    useEffect(() => {
      setSubtotalValueDay(duration?((duration>0)?subtotalValueMonth/duration:0):0);
    },[subtotalValueMonth, duration]);

    const columnsTable: InputColumnTableProps<MedicinesBudgetDetailDTO>[] = React.useMemo(() => [{
        body: (item) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>,
        className: "columnStart",
        header: "Item",
        input: (_item) =>
          <Combobox
            getItems={getMedicines}
            optionLabel="name"
            placeholder="Seleccionar"
            minPanelWidth={600}
            maxPanelWidth={800}
            setValue={(e:ComboMPPDTO) => {
              if(e){
                setNewMedicinesBudgetDetailDTO({
                  ...newMedicinesBudgetDetailDTO,
                  description: e.substanceandpresentation ?? newMedicinesBudgetDetailDTO.description,
                  unitprice: e.cost ?? newMedicinesBudgetDetailDTO.unitprice ?? 0,
                  comboMPPDTO: {
                    ...e,
                    unitsperpackage: e.unitsperpackage || 1,
                    unitsperpackageeditable: !e.unitsperpackage
                  },
                  packageqty: 1,
                  unitsqty: e.unitsperpackage || 1
                });
              }
            }}
            value={newMedicinesBudgetDetailDTO?.comboMPPDTO.name}
          />,
        footerClassName: "font-bold",
        footer: "Subtotales",
      },{
        className: "tableColumn justify-content-center",
        header: "Unid. por envase",
        // Viene del catálogo y define el costo por envase: solo se edita si el catálogo no la trajo y hasta guardar
        input: (_item) => newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackageeditable ?
          <InputTextCustom
            value={newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage??0}
            inputType={EInputTypes.NUMBER}
            inputClassName="text-center"
            onChange={(e: any) => {
              setNewMedicinesBudgetDetailDTO({
                ...newMedicinesBudgetDetailDTO,
                comboMPPDTO: {...newMedicinesBudgetDetailDTO.comboMPPDTO, unitsperpackage: e.target.value ?? 0},
                packageqty: (newMedicinesBudgetDetailDTO.unitsqty && e.target.value)?Math.ceil(newMedicinesBudgetDetailDTO.unitsqty / e.target.value):newMedicinesBudgetDetailDTO.packageqty,
                unitsqty: newMedicinesBudgetDetailDTO.unitsqty?
                  newMedicinesBudgetDetailDTO.unitsqty:
                  ((newMedicinesBudgetDetailDTO.packageqty && e.target.value)?newMedicinesBudgetDetailDTO.packageqty*(e.target.value ?? 0):0)
              });
            }}
          /> :
          <p>{newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage??0}</p>,
        body: (item, index) => item.comboMPPDTO?.unitsperpackageeditable ?
          <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.comboMPPDTO?.unitsperpackage} inputClassName="text-center" onChange={(e: any) => {
            onHandleEditBudget(index, calculateItem({...item, comboMPPDTO: {...item.comboMPPDTO, unitsperpackage: e.target.value ?? 0},
              packageqty: (item.unitsqty && e.target.value)?Math.ceil(item.unitsqty / e.target.value):item.packageqty,
              unitsqty: item.unitsqty?
                item.unitsqty:((item.packageqty && e.target.value)?item.packageqty*(e.target.value ?? 0):0)}
            ));
          }}/> :
          <p>{item.comboMPPDTO?.unitsperpackage}</p>
      }, {
        className: "tableColumn justify-content-center",
        header: "Cant. envases",
        input: (_item) =>
          <InputTextCustom
            value={newMedicinesBudgetDetailDTO.packageqty}
            inputType={EInputTypes.NUMBER}
            inputClassName="text-center"
            onChange={(e: any) => {
              setNewMedicinesBudgetDetailDTO({
                ...newMedicinesBudgetDetailDTO,
                packageqty: e.target.value ?? 0,
                unitsqty: newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage?newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage*(e.target.value ?? 0):0
              });
            }}
            disable={!newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage}
          />,
        body: (item, index) =>
          <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.packageqty} inputClassName="text-center"
            onChange={(e: any) => {
              onHandleEditBudget(index, calculateItem({...item,
                packageqty: e.target.value ?? 0,
                unitsqty: item.comboMPPDTO?.unitsperpackage?item.comboMPPDTO?.unitsperpackage*(e.target.value ?? 0):0})
              );
            }}
            disable={!item.comboMPPDTO?.unitsperpackage}
          />
      }, {
        className: "tableColumn justify-content-center",
        header: "Cant. unidades",
        input: (_item) =>
          <InputTextCustom
            value={newMedicinesBudgetDetailDTO.unitsqty}
            inputType={EInputTypes.NUMBER}
            inputClassName="text-center"
            onChange={(e: any) => {
              setNewMedicinesBudgetDetailDTO({
                ...newMedicinesBudgetDetailDTO,
                unitsqty: e.target.value ?? 0,
                packageqty: Math.ceil((e.target.value ?? 0) / (newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage??1))
              });
            }}
            disable={!newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage}
          />,
        body: (item, index) =>
          <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.unitsqty} inputClassName="text-center"
            onChange={(e: any) => {
              onHandleEditBudget(index, calculateItem({...item,
                unitsqty: e.target.value ?? 0,
                packageqty: Math.ceil((e.target.value ?? 0) / (item.comboMPPDTO?.unitsperpackage??1))
              }));
            }}
            disable={!item.comboMPPDTO?.unitsperpackage}
          />
      }, {
        className: "columnPrice",
        header: <p className="mr-2">{showEarnings?"Costo unitario":"Valor unitario"}</p>,
        input: (_item) => <p className="mr-2">{formatCurrency(newMedicinesBudgetDetailDTO.unitprice as number)}</p>,
        bodyClassName: "justify-content-end",
        body: (item) => <p className="mr-2">{showPriceByItem?formatCurrency(item.unitprice * (showEarnings?1:(item.earnings+100)/100) as number):''}</p>,
      }, {
        className: "tableColumn justify-content-center",
        header: "% Ganancia",
        input: (_item) =>
          <InputTextCustom
            value={localEarnings}
            inputType={EInputTypes.NUMBER}
            inputClassName="text-center"
            onChange={(e: any) => {
              setLocalEarnings(e.target.value ?? 0);
              setNewMedicinesBudgetDetailDTO({
                ...newMedicinesBudgetDetailDTO,
                earnings: e.target.value ?? 0,
              });
            }}
          />,
        body: (item, index) =>
          <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.earnings} inputClassName="text-center" onChange={(e: any) => {
            onHandleEditBudget(index, calculateItem({...item, earnings: e.target.value ?? 0}));
          }}/>,
        show: ()=>showEarnings
      }, {
        className: "columnPrice w-8",
        header: "Valor día",
        input: (_item) => <p>{formatCurrency(newMedicinesBudgetDetailDTO.valueday as number)}</p>,
        bodyClassName: "font-bold justify-content-end",
        body: (item) => showPriceByItem?formatCurrency(item.valueday as number):'',
        footerClassName: "font-bold",
        footer: formatCurrency(subtotalValueDay as number),
        show: () => duration > 0
      },{
        className: `columnPrice ${showEarnings?'w-10':'w-8'} pr-2`,
        header: "Valor total",
        input: (_item) => <p>{formatCurrency(newMedicinesBudgetDetailDTO.valuemonth as number)}</p>,
        bodyClassName: "font-bold justify-content-end",
        body: (item) => showPriceByItem?formatCurrency(item.valuemonth as number):'',
        footerClassName: "font-bold",
        footer: formatCurrency(subtotalValueMonth as number)
      },
    ].filter(col => col !== undefined) as InputColumnTableProps<MedicinesBudgetDetailDTO>[], [duration, newMedicinesBudgetDetailDTO, subtotalValueDay, subtotalValueMonth, showPriceByItem, showEarnings]);

    function onHandleAddBudget(): void {
      if(newMedicinesBudgetDetailDTO.comboMPPDTO == defaultComboMPPDTO) return;
      if(!newMedicinesBudgetDetailDTO.comboMPPDTO.unitsperpackage || !newMedicinesBudgetDetailDTO.unitsqty || !newMedicinesBudgetDetailDTO.packageqty) return;
      onItemsChange?.([...items, newMedicinesBudgetDetailDTO]);
      setNewMedicinesBudgetDetailDTO({...defaultMedicinesBudgetDetailDTO, earnings: localEarnings ?? defaultEarnings ?? 0});
    }

    function onHandleDeleteBudget(index:number): void {
      onItemsChange?.(items.filter((_, itemIndex) => itemIndex !== index));
    }

    function onHandleEditBudget(index:number, newItemValue:MedicinesBudgetDetailDTO): void {
      onItemsChange?.(items.map((item, itemIndex) => itemIndex === index ? newItemValue : item));
    }

    return (
        <div className="nv-card budget-table-card">
            <span className="tableTitle"> Medicamentos </span>
            <InputTable divider dividerClassName="my-0 border-top-1" dividerInFirstItem columns={columnsTable} items={items} onAdd={CRUDBehaviour?onHandleAddBudget:undefined} onDelete={CRUDBehaviour ? (_item, index) => onHandleDeleteBudget(index) : undefined} headerDivClassName="tableHeader align-items-center" inputDivClassName={"align-items-center"} footerDivClassName="tableFooter" divBtnClassName="max-w-54 min-w-54"/>
        </div>
    )
}
